import React, { useEffect } from 'react';
import NavBar from '../../components/NavBar';
import ResponsiveFooter from '../../components/ResponsiveFooter';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';

// Apple-inspired color palette (matching LandingPage.jsx)
const colors = {
  accent: '#1e40af',
  accentHover: '#1e3a8a',
  accentLight: 'rgba(30, 64, 175, 0.1)',
  
  white: '#ffffff',
  gray50: '#f9f9f9',
  gray100: '#f3f3f3',
  gray200: '#e6e6e6',
  gray300: '#d1d1d1',
  gray500: '#8e8e8e',
  gray600: '#6e6e6e',
  gray700: '#636363',
  gray900: '#333333',
};

// Global font styles (matching LandingPage.jsx)
const fontStyles = {
  fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
  heading: {
    fontWeight: 500,
    letterSpacing: '-0.02em',
    color: '#000000',
  },
  subheading: {
    fontWeight: 500,
    letterSpacing: '-0.02em',
    color: '#333333',
  },
  body: {
    fontWeight: 400,
    letterSpacing: '-0.01em',
    color: '#333333',
  },
  medium: {
    fontWeight: 500,
  }
};

// Animation variants
const fadeInVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { 
      duration: 0.6,
      ease: [0.6, -0.05, 0.01, 0.99]
    } 
  }
};

const PrivacyPolicy = () => {

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white text-gray-900 flex flex-col"
      style={{
        color: colors.gray900,
        fontFamily: fontStyles.fontFamily
      }}>
      {/* Navigation */}
      <NavBar 
        activePage=""
      />
      
      {/* Content */}
      <motion.div 
        className="relative py-20 overflow-hidden"
        initial="hidden"
        animate="visible"
        variants={fadeInVariants}
        style={{
          fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
          background: 'linear-gradient(to bottom, white, #f8faff)'
        }}
      >
        {/* Vertical Rails - Aligned with Footer positioning */}
        <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 1 }}>
          <div className="max-w-7xl mx-auto h-full relative" style={{ paddingLeft: 24, paddingRight: 24 }}>
            {/* Left vertical lines - slightly darker */}
            <div className="absolute left-4 top-0 bottom-0 w-px" 
              style={{
                background: 'rgba(30, 64, 175, 0.18)',
                opacity: 0.8
              }} 
            />
            <div className="absolute left-8 top-0 bottom-0 w-px" 
              style={{
                background: 'rgba(30, 64, 175, 0.14)',
                opacity: 0.9
              }} 
            />
            {/* Right vertical lines - slightly darker */}
            <div className="absolute right-4 top-0 bottom-0 w-px" 
              style={{
                background: 'rgba(30, 64, 175, 0.18)',
                opacity: 0.8
              }} 
            />
            <div className="absolute right-8 top-0 bottom-0 w-px" 
              style={{
                background: 'rgba(30, 64, 175, 0.14)',
                opacity: 0.9
              }} 
            />
          </div>
        </div>
        
        <div className="container mx-auto px-6 pt-12 pb-0 relative z-10">
        <div className="max-w-4xl mx-auto">
          <Link to="/" className="inline-flex items-center mb-8 text-sm hover:text-blue-600 transition"
            style={{
              color: colors.gray600,
              fontWeight: 500,
              letterSpacing: '-0.01em'
            }}>
            <ArrowLeft size={16} className="mr-2" />
            Back to Home
          </Link>
          
          <div className="prose prose-lg max-w-none">
            <div className="flex flex-col items-start mb-6">
              <img src="/6.png" alt="SignPact Logo" className="h-12 mb-4" />
              <h1 className="text-4xl font-medium tracking-normal" 
                style={{
                  fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
                  fontWeight: 500,
                  color: '#000000',
                  letterSpacing: '-0.02em',
                  marginTop: '0.25rem'
                }}>
                Privacy Policy.
              </h1>
            </div>
            
            <p className="text-sm italic mb-8" 
              style={{
                color: colors.gray500,
                fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
                fontWeight: 400,
                letterSpacing: '-0.01em'
              }}>
              Last Updated: June 12, 2025
            </p>
            
            <p className="mb-6" style={fontStyles.body}>
              SignPact, Inc. is an AI-powered electronic signature and Contract Lifecycle Management (CLM) platform operating in the United States. This Privacy Policy explains how we collect, use, protect, and share personal information when you use SignPact (the "Service"). SignPact, Inc. is a Delaware C Corporation (EIN: 39-2704088) incorporated on June 12, 2025, and we are committed to handling your data in compliance with applicable laws, including the EU General Data Protection Regulation (GDPR) and the California Consumer Privacy Act (CCPA) as amended by the California Privacy Rights Act (CPRA). We collect personal data only to operate and improve our services, and <strong style={{fontWeight: 500}}>we do not sell your personal information</strong>.
            </p>
            
            <p className="mb-10" style={fontStyles.body}>
              By using SignPact, you acknowledge the practices described in this Privacy Policy.
            </p>
            
            <h2 className="text-2xl mb-4" 
              style={fontStyles.subheading}>
              1. Information We Collect
            </h2>
            
            <p className="mb-4" style={fontStyles.body}>
              We collect several types of information to provide and improve our Service:
            </p>
            
            <ul className="mb-6 list-disc pl-6 space-y-4" style={fontStyles.body}>
              <li>
                <strong style={{fontWeight: 500}}>Account Information:</strong> When you register or use SignPact, we collect information such as your name, email address, password, and optionally your organization or company name. This information is necessary to create and secure your account and allow you to log in and use the Service.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Document and Contract Data:</strong> Our Service allows you to upload documents and contracts, which may include <strong style={{fontWeight: 500}}>personal or business data</strong> about you and other parties (e.g. names, titles, addresses, signatures, company information, financial terms). We store the documents and associated metadata (such as document names, version history, redlines/changes, comments, timestamps of signing or editing, and vendor/client details if you use our vendor management features). <strong style={{fontWeight: 500}}>Sensitive Personal Data:</strong> If you upload documents that contain sensitive personal information (such as financial account numbers, health information, or other sensitive data), we treat it as part of your User Content (see below) and protect it as described in this Policy, but you are responsible for ensuring you have the right to upload such data.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Electronic Signature Information:</strong> When you use SignPact for e-signatures, we collect information related to the signing process. This includes the identities (names, emails) of signers and witnesses you designate, the date and time of signature events, audit trail information (such as IP addresses and device information of signers for security), and any comments or notes made during the signing process.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Invoicing and Transaction Data:</strong> If you use our invoicing features or integrate financial transactions, we process information related to invoices and payments. This may include invoice details (such as invoice number, amount, due date, and description), records of payments or payment status, and business contact information for billers or payers.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Communication Data:</strong> If you communicate with us or through the Service, we collect information such as your contact details and the content of your messages. For example, when you email support or use a chat feature, we will receive your email address and any information you choose to provide. If the Service sends emails on your behalf (for example, sending a contract to a signer or notifying a vendor), we may log those communications (email addresses of recipients, time sent, and success/failure status).
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Usage Data:</strong> We automatically collect certain information about your device and how you interact with SignPact:
                <ul className="mt-2 pl-6 list-disc space-y-2" style={fontStyles.body}>
                  <li><strong style={{fontWeight: 500}}>Technical Data:</strong> This includes your IP address, browser type, device type (e.g. PC or mobile), operating system, and device identifiers.</li>
                  <li><strong style={{fontWeight: 500}}>Usage Analytics:</strong> We record activity logs such as pages or screens viewed, features used (e.g. opening a document, creating a contract, using the redlining tool), the date/time of your activities, crash and error logs, and referral information (what link or website led you to our Service).</li>
                  <li><strong style={{fontWeight: 500}}>Cookies and Similar Technologies:</strong> We use cookies, web beacons, and similar tracking technologies to collect information about your interactions. Cookies are small text files stored on your device that help us recognize you on subsequent visits, keep you logged in, remember your preferences, and understand usage of our Service. For example, we use cookies to maintain your session and to analyze site traffic via Google Analytics (see <em>Analytics</em> below). You have control over cookies through your browser settings and can refuse or delete cookies, though some parts of our Service may become less functional.</li>
                </ul>
              </li>
            </ul>
            
            <h2 className="text-2xl mb-4" 
              style={fontStyles.subheading}>
              2. Use of Cookies and Analytics
            </h2>
            
            <p className="mb-4" style={fontStyles.body}>
              We utilize third-party analytics and tracking tools to better understand how our Service is used and to improve user experience:
            </p>
            
            <ul className="mb-6 list-disc pl-6 space-y-4" style={fontStyles.body}>
              <li>
                <strong style={{fontWeight: 500}}>Google Analytics 4 (GA4):</strong> We use Google Analytics (GA4) to collect anonymized statistical information about usage of our website and app. GA4 may set cookies or use similar identifiers to gather information such as pages visited, time on page, user location (approximate, based on IP which we may anonymize), and interactions with our site. This data helps us analyze trends, track aggregated usage, and improve the platform. Google Analytics provides reports to us and may also use the data it collects as per its own privacy policy. We have configured GA4 in accordance with GDPR guidance (for example, IP anonymization is enabled by default). You can opt out of Google Analytics tracking by using browser opt-out plugins or by disabling cookies (as noted above).
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Google Tag Manager:</strong> We use Google Tag Manager to manage analytics scripts on our website. Tag Manager does not collect personal information itself; it simply loads other scripts (like Google Analytics) and helps ensure they fire correctly.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Cookies for Functionality:</strong> In addition to analytics cookies, we use essential cookies for things like authentication (keeping you logged in), preferences (such as your chosen language or other settings), and to provide certain features (for example, remembering your last opened document). These cookies are necessary for the Service to function properly.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Your Choices:</strong> When you first visit our site, you may be presented with a cookie notice or preferences (depending on legal requirements in your region). You can choose whether to allow analytics cookies. Even after consenting, you can clear cookies or change your browser settings to control cookie behavior. However, please note that disabling all cookies might impair some Service functionality (like staying logged in).
              </li>
            </ul>
            
            <h2 className="text-2xl mb-4" 
              style={fontStyles.subheading}>
              3. How We Use Your Information
            </h2>
            <p className="mb-4" style={fontStyles.body}>
              We use the collected information for various legitimate purposes related to operating our Service. Specifically, we may use your information to:
            </p>
            
            <ul className="mb-6 list-disc pl-6 space-y-4" style={fontStyles.body}>
              <li>
                <strong style={{fontWeight: 500}}>Provide and Maintain the Service:</strong> We process your personal data to create your account, authenticate you when you log in, and enable you to create, edit, and finalize contracts or other documents. For example, we use your uploaded documents and contract data to display them to you and designated viewers or signers, to track changes (redlining), and to facilitate electronic signatures.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Facilitate Contract Collaboration:</strong> Information like document content, metadata, and comments are used to enable collaboration between you and other parties (such as colleagues or counterparties) on the platform. For instance, we use redline edits and comments you make to update the document version and share those changes with authorized viewers.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Enable Vendor Management and Invoicing:</strong> If you use vendor management features, we use the vendor information and contract metadata you input to organize your contracts by vendor or partner, set reminders (e.g. for renewal dates), and integrate with invoicing. For invoicing, we use invoice data to generate invoice documents and track payment status.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Communicate with You:</strong> We use contact information (like your email) to send important notices and updates about signing requests, contract status updates, alerts, and other essential information.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Improve and Develop our Services:</strong> We analyze usage data and feedback to understand how our Service is used and how we can make it better.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Ensure Security and Prevent Fraud:</strong> Information such as IP addresses, device information, and audit logs are used to protect the security of our platform, our users, and the integrity of documents.
              </li>
            </ul>
            
            <h2 className="text-2xl mb-4" 
              style={fontStyles.subheading}>
              4. Legal Bases for Processing (GDPR-specific)
            </h2>
            <p className="mb-4" style={fontStyles.body}>
              If you are located in the European Economic Area (EEA) or United Kingdom, our legal bases for processing your personal data under the GDPR (and UK GDPR) are as follows:
            </p>
            
            <ul className="mb-6 list-disc pl-6 space-y-4" style={fontStyles.body}>
              <li>
                <strong style={{fontWeight: 500}}>Contractual Necessity:</strong> Most data we process is to provide you with the Service you requested under our Terms of Service.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Legitimate Interests:</strong> In some cases, we process data to pursue our legitimate interests in a manner that does not outweigh your privacy rights.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Consent:</strong> We rely on your consent in certain situations, such as for setting non-essential cookies or sending promotional communications.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Legal Obligation:</strong> If we need to process or retain data to comply with a law, such processing is based on legal obligation.
              </li>
            </ul>
            
            <h2 className="text-2xl mb-4" 
              style={fontStyles.subheading}>
              5. How We Share and Disclose Information
            </h2>
            <p className="mb-4" style={fontStyles.body}>
              We understand the importance of keeping your personal information private. We do <strong style={{fontWeight: 500}}>not</strong> sell your personal data to third parties. However, we do share information in certain circumstances:
            </p>
            
            <ul className="mb-6 list-disc pl-6 space-y-4" style={fontStyles.body}>
              <li>
                <strong style={{fontWeight: 500}}>Service Providers (Processors):</strong> We use trusted third-party companies to perform certain services on our behalf.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Document Sharing at Your Direction:</strong> When you send a contract for review or signature, we will share the relevant document with the people you designate.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Business Transfers:</strong> If SignPact is involved in a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Legal Compliance and Protection:</strong> We may disclose your personal information if required to comply with applicable laws or legal processes.
              </li>
            </ul>
            
            <h2 className="text-2xl mb-4" 
              style={fontStyles.subheading}>
              6. Data Retention
            </h2>
            <p className="mb-4" style={fontStyles.body}>
              We retain personal data and user content for only as long as necessary to fulfill the purposes outlined in this Policy, or as required by law:
            </p>
            
            <ul className="mb-6 list-disc pl-6 space-y-4" style={fontStyles.body}>
              <li>
                <strong style={{fontWeight: 500}}>Account Data:</strong> We keep your personal account information as long as your account remains active.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Documents and Contracts:</strong> Documents are stored until you delete them or your account.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Transaction and Audit Logs:</strong> We retain logs of activity for legal evidence of contract actions or to resolve disputes.
              </li>
            </ul>
            
            <h2 className="text-2xl mb-4" 
              style={fontStyles.subheading}>
              7. Data Security
            </h2>
            <p className="mb-4" style={fontStyles.body}>
              We implement a range of technical and organizational measures to protect your data:
            </p>
            
            <ul className="mb-6 list-disc pl-6 space-y-4" style={fontStyles.body}>
              <li>
                <strong style={{fontWeight: 500}}>Encryption:</strong> All communication is encrypted using TLS, and we encrypt sensitive data at rest.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Access Controls:</strong> We restrict access to personal data to authorized personnel who need it.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Secure Development:</strong> We follow secure coding guidelines and best practices.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Monitoring:</strong> We monitor for potential security breaches and have incident response procedures.
              </li>
            </ul>
            
            <h2 className="text-2xl mb-4" 
              style={fontStyles.subheading}>
              8. Your Rights and Choices
            </h2>
            <p className="mb-4" style={fontStyles.body}>
              You have several rights regarding your personal information:
            </p>
            
            <ul className="mb-6 list-disc pl-6 space-y-4" style={fontStyles.body}>
              <li>
                <strong style={{fontWeight: 500}}>Access and Portability:</strong> You can request a copy of your personal information.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Rectification:</strong> You can request correction of inaccurate information.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Deletion:</strong> You can request deletion of your personal data.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Withdrawal of Consent:</strong> You can withdraw consent at any time where processing is based on consent.
              </li>
            </ul>
            
            <h2 className="text-2xl mb-4" 
              style={fontStyles.subheading}>
              9. International Data Transfers
            </h2>
            <p className="mb-6" style={fontStyles.body}>
              SignPact is based in the United States. If you are accessing the Service from outside the U.S., your information will be transferred to and stored on servers in the United States. We take steps to ensure adequate safeguards are in place to protect your data.
            </p>
            
            <h2 className="text-2xl mb-4" 
              style={fontStyles.subheading}>
              10. Children's Privacy
            </h2>
            <p className="mb-6" style={fontStyles.body}>
              SignPact is not directed to children, and we do not knowingly collect personal information from individuals under the age of 13 (or under 16 in certain jurisdictions) without parental consent.
            </p>
            
            <h2 className="text-2xl mb-4" 
              style={fontStyles.subheading}>
              11. Changes to This Privacy Policy
            </h2>
            <p className="mb-6" style={fontStyles.body}>
              We may update this Privacy Policy from time to time. When we make changes, we will revise the "Last Updated" date at the top of this Policy. For significant changes, we will provide more prominent notice.
            </p>
            
            {/* Contact Us section */}
            <div className="border-t my-12 pt-12" style={{borderColor: colors.gray200}}>
              <h2 className="text-2xl mb-4" 
                style={fontStyles.heading}>
                12. Contact Us
              </h2>
              <p className="mb-6" style={fontStyles.body}>
                If you have any questions, concerns, or requests regarding this Privacy Policy or the handling of your personal data, please contact us:
              </p>
              
              <ul className="list-disc pl-6 mb-8 space-y-2" style={fontStyles.body}>
                <li><strong style={{fontWeight: 500}}>Email:</strong> founder@signpact.ai</li>
                <li><strong style={{fontWeight: 500}}>Phone:</strong> (603) 322-0131</li>
                <li><strong style={{fontWeight: 500}}>Mail:</strong> SignPact, Inc., 1349 Hinman, Hanover, NH 03755, US</li>
              </ul>
              
              <p style={fontStyles.body}>
                We will do our best to address your inquiry promptly and thoroughly. Your privacy and trust are important to us, and we welcome your feedback.
              </p>
            </div>
          </div>
        </div>
      </div>
      </motion.div>
      
      {/* Section Divider - Rail-style horizontal lines */}
      <div className="relative w-full">
        <div 
          className="w-full"
          style={{
            borderTop: '1.5px solid rgba(30, 64, 175, 0.25)'
          }}
        />
        {/* Gap between double rails */}
        <div 
          className="w-full"
          style={{
            height: '8px',
            background: 'transparent'
          }}
        />
        {/* Double rail effect - second line */}
        <div 
          className="w-full"
          style={{
            borderTop: '1.5px solid rgba(30, 64, 175, 0.25)'
          }}
        />
      </div>
      
      {/* Footer */}
      <ResponsiveFooter activePage="privacy" />
    </div>
  );
};

export default PrivacyPolicy; 